import { CalendarRecord } from "common/records/Calendar";
import { RootState } from "common/reducers";
import { ids } from "common/selectors/idsManagement.selectors";
import { IdType, Nullable } from "common/types";
import { List } from "immutable";
import { createSelector } from "reselect";

/**
 * Returns the cronofyUrl value
 * @param state The RootState
 * @returns The cronofyUrl value
 */
export const cronofyUrl = (state: RootState) => state.getIn(["calendars", "cronofyUrl"]);

/**
 * Returns the byId
 * @param state The RootState
 * @returns The byId map
 */
export const byId = (state: RootState) => state.getIn(["calendars", "byId"]);

/**
 * Returns the lastSavedById
 * @param state The RootState
 * @returns The lastSavedById map
 */
export const lastSavedById = (state: RootState) => state.getIn(["calendars", "lastSavedById"]);

/**
 * Gets the calendar from byId
 * @param state The RootState
 * @param {{calendarId: number}}props The calendarId
 * @returns The calendar record
 */
export const calendarById: (state: RootState, props: { calendarId: IdType }) => Nullable<CalendarRecord> =
  createSelector([byId, (_, props) => props], (byId, { calendarId }) => byId.get(calendarId));

/**
 * Gets the current list of calendar ids from ids management
 * @param state The RootState
 * @returns list of ids
 */
export const calendarIds = (state: RootState) => ids(state, { modelName: "calendars" });

/**
 * Compares the byId and lastSavedById maps and returns the dirty calendars
 * @param state The RootState
 * @returns The dirty calendars
 */
export const dirtyCalendars = createSelector([byId, calendarIds, lastSavedById], (byId, calendarIds, lastSavedById) => {
  let dirtyRecords = List<CalendarRecord>();
  for (const calendarId of calendarIds) {
    if (typeof calendarId !== "number") continue;
    const calendar = byId.get(calendarId);
    const lastSavedCalendar = lastSavedById.get(calendarId);
    if (calendar && !calendar.equals(lastSavedCalendar)) {
      dirtyRecords = dirtyRecords.push(calendar);
    }
  }
  return dirtyRecords;
});

/**
 * Checks if the calendars are dirty
 * @param state The RootState
 * @returns True if the calendars are dirty, false otherwise
 */
export const calendarsAreDirty = createSelector(
  [byId, calendarIds, lastSavedById],
  (byId, calendarIds, lastSavedById) => {
    return calendarIds.some((calendarId) => {
      if (typeof calendarId !== "number") return false;
      return Boolean(!byId.get(calendarId)?.equals(lastSavedById.get(calendarId)));
    });
  },
);
